import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, { useCallback, useContext } from "react";

import { BG_COLOR, TEXT_COLOR } from "../../constants";
import DatepickerContext from "../../contexts/DatepickerContext";
import { formatDate, nextMonth, previousMonth, classNames as cn } from "../../helpers";
import { Period } from "../../types";

dayjs.extend(isBetween);

interface Props {
    calendarData: {
        date: dayjs.Dayjs;
        days: {
            previous: number[];
            current: number[];
            next: number[];
        };
    };
    onClickPreviousDays: (day: number) => void;
    onClickDay: (day: number) => void;
    onClickNextDays: (day: number) => void;
}

const Days: React.FC<Props> = ({
    calendarData,
    onClickPreviousDays,
    onClickDay,
    onClickNextDays
}) => {
    // Contexts
    const {
        primaryColor,
        period,
        changePeriod,
        dayHover,
        changeDayHover,
        minDate,
        maxDate,
        disabledDates,
        disabledCheckins,
        disabledCheckouts,
        minDays
    } = useContext(DatepickerContext);

    // Functions
    const currentDateClass = useCallback(
        (item: number) => {
            const itemDate = `${calendarData.date.year()}-${calendarData.date.month() + 1}-${
                item >= 10 ? item : "0" + item
            }`;
            if (formatDate(dayjs()) === formatDate(dayjs(itemDate)))
                return TEXT_COLOR["500"][primaryColor as keyof (typeof TEXT_COLOR)["500"]];
            return "";
        },
        [calendarData.date, primaryColor]
    );

    const activeDateData = useCallback(
        (day: number) => {
            const fullDay = `${calendarData.date.year()}-${calendarData.date.month() + 1}-${day}`;
            let className = "";

            if (dayjs(fullDay).isSame(period.start) && dayjs(fullDay).isSame(period.end)) {
                className = ` ${BG_COLOR["500"][primaryColor]} text-white font-medium rounded-full`;
            } else if (dayjs(fullDay).isSame(period.start)) {
                className = ` ${BG_COLOR["500"][primaryColor]} text-white font-medium ${
                    dayjs(fullDay).isSame(dayHover) && !period.end
                        ? "rounded-full"
                        : "rounded-l-full"
                }`;
            } else if (dayjs(fullDay).isSame(period.end)) {
                className = ` ${BG_COLOR["500"][primaryColor]} text-white font-medium ${
                    dayjs(fullDay).isSame(dayHover) && !period.start
                        ? "rounded-full"
                        : "rounded-r-full"
                }`;
            }

            return {
                active: dayjs(fullDay).isSame(period.start) || dayjs(fullDay).isSame(period.end),
                className: className
            };
        },
        [calendarData.date, dayHover, period.end, period.start, primaryColor]
    );

    const hoverClassByDay = useCallback(
        (day: number) => {
            let className = currentDateClass(day);
            const fullDay = `${calendarData.date.year()}-${calendarData.date.month() + 1}-${
                day >= 10 ? day : "0" + day
            }`;

            if (period.start && period.end) {
                if (dayjs(fullDay).isBetween(period.start, period.end, "day", "[)")) {
                    return ` ${BG_COLOR["100"][primaryColor]} ${currentDateClass(
                        day
                    )} dark:bg-white/10`;
                }
            }

            if (!dayHover) {
                return className;
            }

            if (period.start && dayjs(fullDay).isBetween(period.start, dayHover, "day", "[)")) {
                className = ` ${BG_COLOR["100"][primaryColor]} ${currentDateClass(
                    day
                )} dark:bg-white/10`;
            }

            if (period.end && dayjs(fullDay).isBetween(dayHover, period.end, "day", "[)")) {
                className = ` ${BG_COLOR["100"][primaryColor]} ${currentDateClass(
                    day
                )} dark:bg-white/10`;
            }

            if (dayHover === fullDay) {
                const bgColor = BG_COLOR["500"][primaryColor];
                className = `text-white font-medium ${bgColor} ${
                    period.start ? "rounded-r-full" : "rounded-l-full"
                }`;
            }

            return className;
        },
        [calendarData.date, currentDateClass, dayHover, period.end, period.start, primaryColor]
    );

    const isDateTooEarly = useCallback(
        (day: number, type: "current" | "previous" | "next") => {
            if (!minDate) {
                return false;
            }
            const object = {
                previous: previousMonth(calendarData.date),
                current: calendarData.date,
                next: nextMonth(calendarData.date)
            };
            const newDate = object[type as keyof typeof object];
            const formattedDate = newDate.set("date", day);
            return dayjs(formattedDate).isSame(dayjs(minDate), "day")
                ? false
                : dayjs(formattedDate).isBefore(dayjs(minDate));
        },
        [calendarData.date, minDate]
    );

    const isDateTooLate = useCallback(
        (day: number, type: "current" | "previous" | "next") => {
            if (!maxDate) {
                return false;
            }
            const object = {
                previous: previousMonth(calendarData.date),
                current: calendarData.date,
                next: nextMonth(calendarData.date)
            };
            const newDate = object[type as keyof typeof object];
            const formattedDate = newDate.set("date", day);
            return dayjs(formattedDate).isSame(dayjs(maxDate), "day")
                ? false
                : dayjs(formattedDate).isAfter(dayjs(maxDate));
        },
        [calendarData.date, maxDate]
    );

    const isDateGray = useCallback(
        (day: number, type: "current" | "previous" | "next") => {
            if (isDateTooEarly(day, type) || isDateTooLate(day, type)) {
                return true;
            }
            const object = {
                previous: previousMonth(calendarData.date),
                current: calendarData.date,
                next: nextMonth(calendarData.date)
            };
            const newDate = object[type as keyof typeof object];
            const formattedDate = `${newDate.year()}-${newDate.month() + 1}-${
                day >= 10 ? day : "0" + day
            }`;

            const currentDate = dayjs(formattedDate);

            if (disabledDates && (!period.start || period.end)) {
                if (
                    isSingleDisabled(currentDate, disabledDates) &&
                    !isSingleDisabled(currentDate.add(-1, "day"), disabledDates)
                )
                    return true;
            }

            if (disabledDates && (!period.start || (period.start && period.end))) {
                for (let i = 1; i <= minDays; i++) {
                    if (isSingleDisabled(currentDate.add(i, "day"), disabledDates)) {
                        return true;
                    }
                }
            }

            if (!period.start || period.end) {
                if (disabledCheckins.includes(formattedDate)) return true;
            }

            if (period.start && !period.end) {
                if (disabledCheckouts.includes(formattedDate)) {
                    return true;
                }

                const startDate = dayjs(period.start);
                const currentDate = dayjs(formattedDate);
                const minDateRangeStart = startDate.subtract(minDays, "day");
                const minDateRangeEnd = startDate.add(minDays, "day");
                if (
                    currentDate.isAfter(minDateRangeStart) &&
                    currentDate.isBefore(minDateRangeEnd)
                ) {
                    return true;
                }
                if (currentDate.isBefore(startDate)) {
                    return true;
                }
            } else if (period.end && !period.start) {
                const endDate = dayjs(period.end);
                const currentDate = dayjs(formattedDate);
                const minDateRangeStart = endDate.subtract(minDays, "day");
                const minDateRangeEnd = endDate.add(minDays, "day");
                if (
                    currentDate.isAfter(minDateRangeStart) &&
                    currentDate.isBefore(minDateRangeEnd)
                ) {
                    return true;
                }
                if (currentDate.isBefore(endDate)) {
                    return true;
                }
            }

            return false;
        },
        [
            calendarData.date,
            isDateTooEarly,
            disabledCheckouts,
            isDateTooLate,
            period.start,
            disabledDates,
            period.end,
            minDays
        ]
    );

    const isDateDisabled = useCallback(
        (day: number, type: "current" | "previous" | "next") => {
            if (isDateTooEarly(day, type) || isDateTooLate(day, type)) {
                return true;
            }

            const object = {
                previous: previousMonth(calendarData.date),
                current: calendarData.date,
                next: nextMonth(calendarData.date)
            };
            const newDate = object[type as keyof typeof object];
            const formattedDate = `${newDate.year()}-${newDate.month() + 1}-${
                day >= 10 ? day : "0" + day
            }`;

            const currentDate = dayjs(formattedDate);

            if (disabledCheckins && (!period.start || period.end)) {
                if (disabledCheckins.includes(formattedDate)) return true;
            }

            if (disabledCheckouts && period.start && !period.end) {
                if (disabledCheckouts.includes(formattedDate)) return true;
            }

            /* if (!period.start && !period.end) {
                for (let i = 0; i <= minDays; i++) {
                    const checkDate = currentDate.add(i, 'day');
                    if (disabledDates?.some(dateRange =>
                        checkDate.isAfter(dateRange.startDate) && checkDate.isBefore(dateRange.endDate) ||
                        checkDate.isSame(dateRange.startDate) || checkDate.isSame(dateRange.endDate)
                    )) {
                        return true;
                    }
                }
            }*/

            if (disabledDates && period.start && !period.end) {
                const startDate = dayjs(period.start);

                if (currentDate.isBefore(startDate)) {
                    return true;
                }

                const firstDisabledDate = disabledDates?.find(dateRange =>
                    dayjs(dateRange.startDate).isAfter(startDate)
                );

                if (firstDisabledDate) {
                    if (currentDate.isAfter(firstDisabledDate.startDate)) {
                        return true;
                    }
                }

                if (
                    isSingleDisabled(currentDate, disabledDates) &&
                    !isSingleDisabled(currentDate.add(-1, "day"), disabledDates)
                )
                    return false;
            }

            if (disabledDates && (!period.start || (period.start && period.end))) {
                for (let i = 1; i <= minDays - 1; i++) {
                    //last day is ok to be disabled (checkout only)
                    if (isSingleDisabled(currentDate.add(i, "day"), disabledDates)) {
                        return true;
                    }
                }
            }

            if (period.start && !period.end) {
                const startDate = dayjs(period.start);
                const minDateRangeStart = startDate.subtract(minDays, "day");
                const minDateRangeEnd = startDate.add(minDays, "day");

                if (currentDate.isSame(startDate)) {
                    return false;
                }

                if (
                    currentDate.isAfter(minDateRangeStart) &&
                    currentDate.isBefore(minDateRangeEnd)
                ) {
                    return true;
                }
                if (currentDate.isBefore(startDate)) {
                    return true;
                }

                const firstDisabledDate = disabledDates?.find(dateRange =>
                    dayjs(dateRange.startDate).isAfter(startDate)
                );

                if (firstDisabledDate) {
                    if (currentDate.isAfter(firstDisabledDate.startDate)) {
                        return true;
                    }
                }
            } else if (period.end && !period.start) {
                const endDate = dayjs(period.end);
                const minDateRangeStart = endDate.subtract(minDays, "day");
                const minDateRangeEnd = endDate.add(minDays, "day");
                if (
                    currentDate.isAfter(minDateRangeStart) &&
                    currentDate.isBefore(minDateRangeEnd)
                ) {
                    return true;
                }
                if (currentDate.isBefore(endDate)) {
                    return true;
                }
            }

            if (!disabledDates || (Array.isArray(disabledDates) && !disabledDates.length)) {
                return false;
            }

            const retVal = isSingleDisabled(formattedDate, disabledDates);

            return retVal;
        },
        [
            calendarData.date,
            isDateTooEarly,
            isDateTooLate,
            period.start,
            disabledDates,
            period.end,
            minDays
        ]
    );

    const actuallyDisabled = useCallback(
        (day: number, type: "current" | "previous" | "next") => {
            if (!disabledDates) return false;

            const object = {
                previous: previousMonth(calendarData.date),
                current: calendarData.date,
                next: nextMonth(calendarData.date)
            };
            const newDate = object[type as keyof typeof object];
            const formattedDate = `${newDate.year()}-${newDate.month() + 1}-${
                day >= 10 ? day : "0" + day
            }`;

            const currentDate = dayjs(formattedDate);

            if (disabledDates) {
                if (!isSingleDisabled(currentDate.add(-1, "day"), disabledDates)) return false;
            }

            const retVal = isSingleDisabled(formattedDate, disabledDates);

            return retVal;
        },
        [calendarData.date, disabledDates]
    );

    function isSingleDisabled(
        formattedDate: string | number | Date | dayjs.Dayjs | null | undefined,
        disabledDates: any[]
    ) {
        let matchingCount = 0;

        disabledDates?.forEach(dateRange => {
            if (
                dayjs(formattedDate).isAfter(dateRange.startDate) &&
                dayjs(formattedDate).isBefore(dateRange.endDate)
            ) {
                matchingCount++;
            }
            if (
                dayjs(formattedDate).isSame(dateRange.startDate) ||
                dayjs(formattedDate).isSame(dateRange.endDate)
            ) {
                matchingCount++;
            }
        });

        return matchingCount > 0;
    }

    const buttonClass = useCallback(
        (day: number, type: "current" | "next" | "previous") => {
            const baseClass = "flex items-center justify-center w-12 h-12 lg:w-10 lg:h-10";
            if (type === "current") {
                return cn(
                    baseClass,
                    !activeDateData(day).active
                        ? hoverClassByDay(day)
                        : activeDateData(day).className,
                    (actuallyDisabled(day, type) ||
                        (isDateDisabled(day, type) && !isDateGray(day, type))) &&
                        hoverClassByDay(day) === "" &&
                        "line-through text-gray-300",
                    !actuallyDisabled(day, type) &&
                        isDateDisabled(day, type) &&
                        isDateGray(day, type) &&
                        hoverClassByDay(day) === "" &&
                        "text-gray-200"
                );
            }
            //previous and next months on calendar, invisible
            return cn(baseClass, "invisible");
        },
        [activeDateData, hoverClassByDay, isDateDisabled, actuallyDisabled, isDateGray]
    );

    const checkIfHoverPeriodContainsDisabledPeriod = useCallback(
        (hoverPeriod: Period) => {
            if (!Array.isArray(disabledDates)) {
                return false;
            }

            // Check if the end period ends in a day that is after a non-disabled day
            if (isSingleDisabled(hoverPeriod.end, disabledDates)) {
                const nextDay = dayjs(hoverPeriod.end).add(1, "day");
                if (!isSingleDisabled(nextDay.format("YYYY-MM-DD"), disabledDates)) {
                    return false;
                }
            }

            for (let i = 0; i < disabledDates.length; i++) {
                if (
                    (dayjs(hoverPeriod.start).isBefore(disabledDates[i].startDate) ||
                        dayjs(hoverPeriod.start).isSame(disabledDates[i].startDate)) &&
                    (dayjs(hoverPeriod.end).isAfter(disabledDates[i].endDate) ||
                        dayjs(hoverPeriod.end).isSame(disabledDates[i].endDate))
                ) {
                    return true;
                }
            }

            return false;
        },
        [disabledDates]
    );

    const getMetaData = useCallback(() => {
        return {
            previous: previousMonth(calendarData.date),
            current: calendarData.date,
            next: nextMonth(calendarData.date)
        };
    }, [calendarData.date]);

    const hoverDay = useCallback(
        (day: number, type: string) => {
            const object = getMetaData();
            const newDate = object[type as keyof typeof object];
            const newHover = `${newDate.year()}-${newDate.month() + 1}-${
                day >= 10 ? day : "0" + day
            }`;

            /* if (period.start && !period.end) {
                const hoverPeriod = { ...period, end: newHover };
                if (dayjs(newHover).isBefore(dayjs(period.start))) {
                    hoverPeriod.start = newHover;
                    hoverPeriod.end = period.start;
                    if (!checkIfHoverPeriodContainsDisabledPeriod(hoverPeriod)) {
                        changePeriod({
                            start: null,
                            end: period.start
                        });
                    }
                }
                if (!checkIfHoverPeriodContainsDisabledPeriod(hoverPeriod)) {
                    changeDayHover(newHover);
                }
            }*/

            if (!period.start && period.end) {
                const hoverPeriod = { ...period, start: newHover };
                if (dayjs(newHover).isAfter(dayjs(period.end))) {
                    hoverPeriod.start = period.end;
                    hoverPeriod.end = newHover;
                    if (!checkIfHoverPeriodContainsDisabledPeriod(hoverPeriod)) {
                        changePeriod({
                            start: period.end,
                            end: null
                        });
                    }
                }
                if (!checkIfHoverPeriodContainsDisabledPeriod(hoverPeriod)) {
                    changeDayHover(newHover);
                }
            }
        },
        [
            changeDayHover,
            changePeriod,
            checkIfHoverPeriodContainsDisabledPeriod,
            getMetaData,
            period
        ]
    );

    const handleClickDay = useCallback(
        (day: number, type: "previous" | "current" | "next") => {
            function continueClick() {
                if (type === "previous") {
                    onClickPreviousDays(day);
                }

                if (type === "current") {
                    onClickDay(day);
                }

                if (type === "next") {
                    onClickNextDays(day);
                }
            }

            if (disabledDates?.length) {
                const object = getMetaData();
                const newDate = object[type as keyof typeof object];
                const clickDay = `${newDate.year()}-${newDate.month() + 1}-${
                    day >= 10 ? day : "0" + day
                }`;

                if (period.start && !period.end) {
                    if (dayjs(clickDay).isSame(dayHover)) continueClick();
                    else {
                        if (dayjs(period.start).isBefore(clickDay)) {
                            !checkIfHoverPeriodContainsDisabledPeriod({
                                start: period.start,
                                end: clickDay
                            }) && continueClick();
                        } else {
                            !checkIfHoverPeriodContainsDisabledPeriod({
                                start: clickDay,
                                end: period.start
                            }) && continueClick();
                        }
                    }
                } else if (!period.start && period.end) {
                    if (dayjs(clickDay).isSame(dayHover)) continueClick();
                    else {
                        !checkIfHoverPeriodContainsDisabledPeriod({
                            start: clickDay,
                            end: period.end
                        }) && continueClick();
                    }
                } else {
                    continueClick();
                }
            } else {
                continueClick();
            }
        },
        [
            dayHover,
            disabledDates?.length,
            getMetaData,
            onClickDay,
            onClickNextDays,
            onClickPreviousDays,
            period.end,
            period.start
        ]
    );

    return (
        <div className="grid grid-cols-7 gap-y-0.5 my-1">
            {calendarData.days.previous.map((item, index) => (
                <button
                    type="button"
                    key={`previous-${index}`}
                    disabled={isDateDisabled(item, "previous")}
                    className={`${buttonClass(item, "previous")}`}
                    onClick={() => handleClickDay(item, "previous")}
                    onMouseOver={() => {
                        hoverDay(item, "previous");
                    }}
                >
                    {item}
                </button>
            ))}

            {calendarData.days.current.map((item, index) => (
                <button
                    type="button"
                    key={`current-${index}`}
                    disabled={isDateDisabled(item, "current")}
                    className={`${buttonClass(item, "current")}`}
                    onClick={() => handleClickDay(item, "current")}
                    onMouseOver={() => {
                        hoverDay(item, "current");
                    }}
                >
                    {item}
                </button>
            ))}

            {calendarData.days.next.map((item, index) => (
                <button
                    type="button"
                    key={`next-${index}`}
                    disabled={isDateDisabled(item, "next")}
                    className={`${buttonClass(item, "next")}`}
                    onClick={() => handleClickDay(item, "next")}
                    onMouseOver={() => {
                        hoverDay(item, "next");
                    }}
                >
                    {item}
                </button>
            ))}
        </div>
    );
};

export default Days;
